import { useState, useMemo } from 'react'
import { format, subDays } from 'date-fns'
import {
  Users,
  UserCheck,
  FileText,
  MessageSquare,
  TrendingUp,
  Filter,
  Download,
  AlertCircle
} from 'lucide-react'
import { useCompanyAnalytics, AnalyticsDateRange } from '@/hooks/useAnalytics'
import { StatCard } from './StatCard'
import { LineChart } from './LineChart'
import { PieChart } from './PieChart'
import { BarChart } from './BarChart'
import { cn } from '@/lib/utils'

interface CompanyAnalyticsProps {
  companyId?: string
  className?: string
}

export function CompanyAnalytics({ companyId, className }: CompanyAnalyticsProps) {
  const [dateRange, setDateRange] = useState<AnalyticsDateRange>({})

  // Default date range: last 30 days
  const defaultDateRange = useMemo(() => {
    const endDate = new Date()
    const startDate = subDays(endDate, 30)

    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd')
    }
  }, [])

  const { data: analytics, loading, error, refetch } = useCompanyAnalytics(
    companyId,
    Object.keys(dateRange).length > 0 ? dateRange : defaultDateRange
  )

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRange(prev => ({ ...prev, startDate: e.target.value }))
  }

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRange(prev => ({ ...prev, endDate: e.target.value }))
  }

  const handleResetFilter = () => {
    setDateRange({})
  }

  // Export to CSV function
  const handleExportToCSV = () => {
    if (!analytics) return

    const csvData = [
      ['Metric', 'Value'],
      ['Total Users', analytics.totalUsers.toString()],
      ['Active Users', analytics.activeUsers.toString()],
      ['Total Content', analytics.totalContent.toString()],
      ['Total Chats', analytics.totalChats.toString()],
      [],
      ['Content by Type', 'Count'],
      ...Object.entries(analytics.contentByType).map(([type, count]) => [type, count.toString()]),
      [],
      ['User Activity', 'Count'],
      ...analytics.userActivity.map(({ date, count }) => [date, count.toString()])
    ]

    const csvContent = csvData.map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', `company-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`)
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Prepare chart data
  const lineChartData = useMemo(() => {
    if (!analytics) return []

    return analytics.userActivity.map(item => ({
      name: format(new Date(item.date), 'MMM d'),
      'User Activity': item.count
    }))
  }, [analytics])

  const pieChartData = useMemo(() => {
    if (!analytics) return []

    return Object.entries(analytics.contentByType).map(([type, count]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1),
      value: count
    }))
  }, [analytics])

  // Mock most viewed content data (replace with real API when available)
  const mostViewedContent = [
    { id: 1, title: 'AI Workshop Introduction', type: 'presentation', views: 245 },
    { id: 2, title: 'Company Guidelines 2024', type: 'pdf', views: 189 },
    { id: 3, title: 'Product Demo Video', type: 'video', views: 156 },
    { id: 4, title: 'Team Structure Overview', type: 'presentation', views: 134 },
    { id: 5, title: 'Q4 Strategy Document', type: 'pdf', views: 98 }
  ]

  // Calculate growth trends (mock data - replace with real calculations)
  const growthTrends = {
    totalUsers: { value: 12, isPositive: true },
    activeUsers: { value: 8, isPositive: true },
    totalContent: { value: 15, isPositive: true },
    totalChats: { value: 22, isPositive: true }
  }

  // Loading state
  if (loading) {
    return (
      <div className={cn("space-y-6", className)}>
        {/* Date Range Filter Skeleton */}
        <div className="bg-card border border-border p-4 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="h-10 bg-muted rounded animate-pulse w-32"></div>
            <div className="h-9 bg-muted rounded animate-pulse w-40"></div>
            <div className="h-9 bg-muted rounded animate-pulse w-40"></div>
            <div className="h-9 bg-muted rounded animate-pulse w-32 ml-auto"></div>
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card border border-border p-6 rounded-lg animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="h-4 bg-muted rounded w-24"></div>
                <div className="h-8 bg-muted rounded w-8"></div>
              </div>
              <div className="h-8 bg-muted rounded w-16 mb-2"></div>
              <div className="h-3 bg-muted rounded w-32"></div>
            </div>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border p-6 rounded-lg">
            <div className="h-6 bg-muted rounded w-40 mb-4 animate-pulse"></div>
            <div className="h-64 bg-muted/30 rounded animate-pulse"></div>
          </div>
          <div className="bg-card border border-border p-6 rounded-lg">
            <div className="h-6 bg-muted rounded w-40 mb-4 animate-pulse"></div>
            <div className="h-64 bg-muted/30 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Most Viewed Content Skeleton */}
        <div className="bg-card border border-border p-6 rounded-lg">
          <div className="h-6 bg-muted rounded w-48 mb-4 animate-pulse"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-muted/50 rounded animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={cn("bg-destructive/10 border border-destructive/20 p-6 rounded-lg", className)}>
        <div className="flex items-start gap-4">
          <div className="p-2 bg-destructive/20 rounded-lg">
            <AlertCircle className="w-5 h-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load Analytics</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header with Date Range Filter and Export */}
      <div className="bg-card border border-border p-4 rounded-lg">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Filter className="w-4 h-4" />
            <span>Date Range:</span>
          </div>

          <div className="flex flex-wrap items-center gap-3 flex-1">
            <div className="flex items-center gap-2">
              <label htmlFor="start-date" className="text-sm text-muted-foreground">
                From:
              </label>
              <input
                id="start-date"
                type="date"
                value={dateRange.startDate || defaultDateRange.startDate}
                onChange={handleStartDateChange}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
              />
            </div>

            <div className="flex items-center gap-2">
              <label htmlFor="end-date" className="text-sm text-muted-foreground">
                To:
              </label>
              <input
                id="end-date"
                type="date"
                value={dateRange.endDate || defaultDateRange.endDate}
                onChange={handleEndDateChange}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
              />
            </div>

            {(dateRange.startDate || dateRange.endDate) && (
              <button
                onClick={handleResetFilter}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted"
              >
                Reset
              </button>
            )}
          </div>

          <button
            onClick={handleExportToCSV}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            Export to CSV
          </button>
        </div>
      </div>

      {/* Company-wide Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={analytics?.totalUsers || 0}
          icon={Users}
          description="All registered users"
          trend={growthTrends.totalUsers}
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />

        <StatCard
          title="Active Users"
          value={analytics?.activeUsers || 0}
          icon={UserCheck}
          description="Users active in period"
          trend={growthTrends.activeUsers}
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />

        <StatCard
          title="Total Content"
          value={analytics?.totalContent || 0}
          icon={FileText}
          description="Files and presentations"
          trend={growthTrends.totalContent}
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />

        <StatCard
          title="Total Chats"
          value={analytics?.totalChats || 0}
          icon={MessageSquare}
          description="AI conversations"
          trend={growthTrends.totalChats}
          className="cursor-pointer hover:border-primary/50 transition-colors"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Trend */}
        <div className="bg-card border border-border p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">User Growth Trend</h3>
            <TrendingUp className="w-5 h-5 text-muted-foreground" />
          </div>
          {lineChartData.length > 0 ? (
            <LineChart
              data={lineChartData}
              lines={[{ dataKey: 'User Activity', name: 'Active Users', color: 'hsl(var(--primary))' }]}
              height={280}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No activity data available
            </div>
          )}
        </div>

        {/* Content Distribution */}
        <div className="bg-card border border-border p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Content by Type</h3>
            <FileText className="w-5 h-5 text-muted-foreground" />
          </div>
          {pieChartData.length > 0 ? (
            <PieChart
              data={pieChartData}
              height={280}
              innerRadius={60}
              outerRadius={100}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No content data available
            </div>
          )}
        </div>
      </div>

      {/* User Activity Heatmap (using Bar Chart for now) */}
      <div className="bg-card border border-border p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">User Activity</h3>
            <p className="text-sm text-muted-foreground mt-1">Daily activity over the selected period</p>
          </div>
          <Users className="w-5 h-5 text-muted-foreground" />
        </div>
        {lineChartData.length > 0 ? (
          <BarChart
            data={lineChartData}
            bars={[{ dataKey: 'User Activity', name: 'Active Users', color: 'hsl(var(--primary))' }]}
            height={300}
          />
        ) : (
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            No activity data available
          </div>
        )}
      </div>

      {/* Most Viewed Content */}
      <div className="bg-card border border-border p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Most Viewed Content</h3>
            <p className="text-sm text-muted-foreground mt-1">Top 5 most accessed items</p>
          </div>
          <FileText className="w-5 h-5 text-muted-foreground" />
        </div>

        <div className="space-y-3">
          {mostViewedContent.map((content, index) => (
            <div
              key={content.id}
              className={cn(
                "flex items-center gap-4 p-4 rounded-lg transition-colors",
                index === 0 ? "bg-primary/5 border border-primary/10" : "bg-muted/30 hover:bg-muted/50"
              )}
            >
              <div className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold",
                index === 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              )}>
                {index + 1}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-foreground truncate">
                    {content.title}
                  </p>
                  {index === 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full flex-shrink-0">
                      Most Popular
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="capitalize">{content.type}</span>
                  <span>•</span>
                  <span>{content.views} views</span>
                </div>
              </div>

              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <TrendingUp className="w-4 h-4" />
                <span>{content.views}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
